import { lazy } from "react";

// Set before reloading for a new build, cleared by the next chunk that loads
const RELOAD_MARK = "scopex:reloadedForNewBuild";

// sessionStorage can throw (storage blocked). Without it nothing reloads: the error page shows.
const markReload = () => {
  try {
    sessionStorage.setItem(RELOAD_MARK, "1");
    return true;
  } catch {
    return false;
  }
};

const reloadMarked = () => {
  try {
    return sessionStorage.getItem(RELOAD_MARK) === "1";
  } catch {
    return true;
  }
};

const clearReloadMark = () => {
  try {
    sessionStorage.removeItem(RELOAD_MARK);
  } catch {
    // nothing stored, nothing to clear
  }
};

// How a chunk that can't be loaded fails: the browser rejects the import with a TypeError worded
// its own way (Chrome and Edge, Firefox, Safari), and Vite rejects with its own error when the
// chunk's stylesheet is missing
const MISSING_CHUNK =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS/;

/** Whether a page's code couldn't be loaded (its chunk is gone or unreachable). */
export const isMissingChunk = error => MISSING_CHUNK.test(error?.message ?? "");

/**
 * Wraps a dynamic import so a tab still running an old build gets the new one. A deploy rebuilds
 * `apps/frontend/build` and deletes the old chunks, so such a tab can't load a page it hasn't
 * loaded yet, e.g. the dashboard after logging in on a login page opened before the deploy.
 * Reloading fetches the new build. It reloads once: not while offline, where the reload would show
 * the browser's offline page instead of the app, and not again right after a reload, in case the
 * chunk is missing from the new build too. Then the error reaches the ErrorBoundary as before.
 */
export const reloadOnStaleBuild = load => () =>
  load().then(
    module => {
      clearReloadMark();
      return module;
    },
    error => {
      if (!isMissingChunk(error) || !navigator.onLine || reloadMarked()) throw error;
      if (!markReload()) throw error;
      window.location.reload();
      // The page goes away; keep React waiting instead of showing the error page first
      return new Promise(() => {});
    },
  );

/**
 * A page whose code loads on first use, as a chunk of its own (`React.lazy`). `preload()` fetches
 * it ahead of time; once loaded, the page opens without waiting, also after the connection drops
 * (a walker on site) or a deploy deleted the old build's chunks.
 */
export default function lazyPage(load) {
  const Page = lazy(reloadOnStaleBuild(load));
  // A failed preload costs nothing: opening the page loads it again.
  Page.preload = () => load().catch(() => {});
  return Page;
}
